import { type KeyboardEvent, type MouseEvent, useRef } from "react";

import { useMove } from "@react-aria/interactions";

import { cn } from "@btnet/utilities-core";

/** Drag distance that dismisses the sheet, whichever of the two is larger. */
const MIN_CLOSE_DISTANCE_PX = 64;
const CLOSE_DISTANCE_RATIO = 0.25;
/** A short but fast flick dismisses too, so the gesture doesn't require a full-height drag. */
const FLICK_VELOCITY_PX_PER_MS = 0.5;
const MIN_FLICK_DISTANCE_PX = 24;
/** Past this gap since the last move, the finger had stopped and the velocity no longer counts. */
const VELOCITY_STALE_AFTER_MS = 100;

export interface IDraggableSheetHandleProps {
  className: string;
  /** Current drag distance, in px below the sheet's resting position. Never negative. */
  onOffsetChange: (offsetPx: number) => void;
  /** Whether a drag is in progress — the sheet drops its transform transition while it is. */
  onDraggingChange: (isDragging: boolean) => void;
  /**
   * Dismiss the sheet. `event` is whatever DOM event triggered it, or none for a drag, which
   * has no single event to attribute the close to.
   */
  onDismiss: (
    event?: KeyboardEvent<HTMLDivElement> | MouseEvent<HTMLDivElement>,
  ) => void;
}

/**
 * The interactive grabber: drag it down to dismiss the sheet, or tap it / press
 * Enter, Space, or ArrowDown.
 *
 * The drag offset lives in `useBottomSheet` rather than here because it drives the drawer's
 * `contentWrapperStyle`, which is set on an ancestor this handle can't reach; hence the
 * callbacks rather than a return value.
 */
export function DraggableSheetHandle({
  className,
  onOffsetChange,
  onDraggingChange,
  onDismiss,
}: IDraggableSheetHandleProps) {
  const handleRef = useRef<HTMLDivElement>(null);

  // The gesture reads and writes these synchronously across move events, so they can't live in
  // state — onMoveEnd needs the final values in the same tick it decides whether to dismiss.
  const gestureRef = useRef({
    /** Clamped to >= 0: the sheet follows the finger down but never lifts above its resting position. */
    offsetPx: 0,
    /** Unclamped, so an upward keyboard arrow is distinguishable from a downward one. */
    rawOffsetPx: 0,
    velocityPxPerMs: 0,
    lastSampleTimeMs: 0,
    lastSampleOffsetPx: 0,
    sheetHeightPx: 0,
    /** Suppresses the tap-to-close click that follows a drag's pointerup. */
    didDrag: false,
  });

  const { moveProps } = useMove({
    onMoveStart: () => {
      const wrapper = handleRef.current?.closest(".ant-drawer-content-wrapper");
      gestureRef.current = {
        offsetPx: 0,
        rawOffsetPx: 0,
        velocityPxPerMs: 0,
        lastSampleTimeMs: performance.now(),
        lastSampleOffsetPx: 0,
        sheetHeightPx: wrapper?.getBoundingClientRect().height ?? 0,
        didDrag: true,
      };
      onDraggingChange(true);
    },
    onMove: (e) => {
      const gesture = gestureRef.current;
      gesture.rawOffsetPx += e.deltaY;
      gesture.offsetPx = Math.max(0, gesture.rawOffsetPx);

      const nowMs = performance.now();
      const elapsedMs = nowMs - gesture.lastSampleTimeMs;
      if (elapsedMs > 0) {
        gesture.velocityPxPerMs =
          (gesture.offsetPx - gesture.lastSampleOffsetPx) / elapsedMs;
        gesture.lastSampleTimeMs = nowMs;
        gesture.lastSampleOffsetPx = gesture.offsetPx;
      }

      onOffsetChange(gesture.offsetPx);
    },
    onMoveEnd: (e) => {
      const gesture = gestureRef.current;
      const { offsetPx, rawOffsetPx, sheetHeightPx } = gesture;

      const settleToRest = () => {
        onDraggingChange(false);
        onOffsetChange(0);
        gesture.offsetPx = 0;
      };

      if (e.pointerType === "keyboard") {
        // Arrow keys move by a synthetic 1px, which no distance threshold would ever clear,
        // so a downward press dismisses outright. Sideways and upward presses do nothing.
        // The keyboard path produces no click, so clear didDrag here instead.
        gesture.didDrag = false;
        settleToRest();
        if (rawOffsetPx > 0) {
          onDismiss();
        }
        return;
      }

      // Velocity is only meaningful if the finger was still moving at release. Holding
      // steady before letting go would otherwise flick the sheet closed on a stale reading.
      const isVelocityStale =
        performance.now() - gesture.lastSampleTimeMs > VELOCITY_STALE_AFTER_MS;
      const isFlick =
        !isVelocityStale &&
        offsetPx >= MIN_FLICK_DISTANCE_PX &&
        gesture.velocityPxPerMs > FLICK_VELOCITY_PX_PER_MS;

      const closeDistancePx = Math.max(
        MIN_CLOSE_DISTANCE_PX,
        sheetHeightPx * CLOSE_DISTANCE_RATIO,
      );

      if (offsetPx > closeDistancePx || isFlick) {
        onDismiss();
        return;
      }

      // Short of the threshold: drop back to rest and let the drawer's own transform
      // transition carry it, which reads as a spring-back.
      settleToRest();
    },
  });

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    // Arrow keys belong to useMove; only add the activation keys a role="button" implies.
    moveProps.onKeyDown?.(e);
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onDismiss(e);
    }
  };

  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    // Every pointerup on the handle is followed by a click, including the one that ends a
    // drag. Without this guard, releasing a below-threshold drag would snap the sheet back
    // and then immediately close it anyway.
    if (gestureRef.current.didDrag) {
      gestureRef.current.didDrag = false;
      return;
    }
    onDismiss(e);
  };

  return (
    <div
      ref={handleRef}
      className={cn(className, "bt-bottom-sheet-handle-draggable")}
      role="button"
      aria-label="Drag handle"
      tabIndex={0}
      data-testid="bt-bottom-sheet-handle"
      {...moveProps}
      onKeyDown={handleKeyDown}
      onClick={handleClick}
    />
  );
}
